import React, { useState, useRef, useEffect } from "react";
import axios from "axios";

import { Container, Button, Form, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import {
  STUDENT_PROFILE_API,
  TUTOR_PROFILE_SETTINGS_API,
} from "../../../api/api";

import { FaEye, FaEyeSlash } from "react-icons/fa";

import { initialsFromName } from "../../../utils/getInitials";
import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentProfile.css";

const StudentProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef();

  const [activeTab, setActiveTab] = useState("profile");

  // profile state
  const [profileData, setProfileData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    dob: "",
    college: "",
    qualification: "",
    yearOfPassing: "",
    address: "",
    pincode: "",
  });

  const [originalProfileData, setOriginalProfileData] = useState(profileData); // Track original profileData
  // profile photo change
  const [avatar, setAvatar] = useState(null);
  const [originalAvatar, setOriginalAvatar] = useState(null);

  // password state
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  //loading
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  // Handle input change for profile
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatar(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle input change for password
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleShow = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleProfileCancel = () => {
    setProfileData(originalProfileData);
    setAvatar(originalAvatar);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isProfileChanged = () => {
    const fieldsChanged = Object.keys(profileData).some(
      (key) => profileData[key] !== originalProfileData[key],
    );

    const imageChanged = fileInputRef.current?.files[0];

    return fieldsChanged || imageChanged;
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      navigate("/signin");

      return;
    }

    const fetchProfile = async () => {
      try {
        const response = await axios.post(
          STUDENT_PROFILE_API.STUDENT_GET_PROFILE,
          { user_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200 && data.data) {
          const user = data.data;

          const fetchedProfile = {
            fullName: user.full_name || "",
            email: user.user_email || "",
            phoneNumber: user.phone_number || "",
            gender: user.gender || "",
            dob: user.date_of_birth?.split("T")[0] || "",
            college: user.college || "",
            qualification: user.qualification || "",
            yearOfPassing: user.year_of_passing || "",
            address: user.address || "",
            pincode: user.pincode || "",
          };

          setProfileData(fetchedProfile);
          setOriginalProfileData(fetchedProfile);

          setAvatar(user.profile_image || null);
          setOriginalAvatar(user.profile_image || null);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      }
    };

    fetchProfile();
  }, [navigate]);

  const validateProfile = () => {
    const {
      // fullName,
      // phoneNumber,
      dob,
      college,
      qualification,
      yearOfPassing,
      pincode,
      address,
    } = profileData;

    // if (!fullName || !/^[a-zA-Z ]+$/.test(fullName)) {
    //   showErrorToast("Full Name is required alphabets only.");
    //   return false;
    // }

    // if (phoneNumber && !/^[6-9]\d{9}$/.test(phoneNumber)) {
    //   showErrorToast("Enter a valid 10-digit phone number.");
    //   return false;
    // }

    const today = new Date().toISOString().split("T")[0];
    if (dob > today) {
      showErrorToast(`DOB date cannot be in the future.`);
      return false;
    }

    if (college && !/^[a-zA-Z .,'&()/-]+$/.test(college)) {
      showErrorToast("College should contain only letters.");
      return false;
    }

    if (qualification && !/^[a-zA-Z .,'&()-]+$/.test(qualification)) {
      showErrorToast("Qualification should contain only letters.");
      return false;
    }

    if (yearOfPassing && !/^\d{4}$/.test(yearOfPassing)) {
      showErrorToast("Year of Passing should be 4 digits.");
      return false;
    }

    if (pincode && !/^\d{6}$/.test(pincode)) {
      showErrorToast("Pincode should be 6 digits.");
      return false;
    }

    if (address && !/^[a-zA-Z0-9 ,.'/-]+$/.test(address)) {
      showErrorToast("Address contains invalid characters.");
      return false;
    }

    return true;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    if (!validateProfile()) return;

    if (!isProfileChanged()) {
      showErrorToast("No changes detected!");
      return;
    }

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!token || !userId) {
      setIsProfileLoading(false);
      navigate("/signin");

      return;
    }

    const formData = new FormData();
    formData.append("user_id", userId);
    // formData.append("full_name", profileData.fullName);
    // formData.append("phone_number", profileData.phoneNumber);
    formData.append("gender", profileData.gender);
    formData.append("date_of_birth", profileData.dob);
    formData.append("college", profileData.college);
    formData.append("qualification", profileData.qualification);
    formData.append("year_of_passing", profileData.yearOfPassing);
    formData.append("address", profileData.address);
    formData.append("pincode", profileData.pincode);

    // Only append if a new avatar is uploaded
    if (fileInputRef.current?.files[0]) {
      formData.append("profile_image", fileInputRef.current.files[0]);
    }

    setIsProfileLoading(true);
    try {
      const response = await axios.post(
        STUDENT_PROFILE_API.UPDATE_PROFILE,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        setOriginalProfileData(profileData);
        setOriginalAvatar(avatar);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      // Server responded with non-2xx status (like 400, 500)

      // CHECK (expiry / invalid token)
      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    setShowPassword({
      current: false,
      new: false,
      confirm: false,
    });
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorToast("New password and confirm password do not match!");
      return;
    }

    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setIsPasswordLoading(false);
      navigate("/signin");

      return;
    }

    const passwordPayload = {
      user_id: userId,
      currentPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
      confirmNewPassword: passwordData.confirmPassword,
    };

    setIsPasswordLoading(true);

    try {
      const response = await axios.post(
        TUTOR_PROFILE_SETTINGS_API.TUTOR_CHANGE_PASSWORD,
        passwordPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = response.data;

      if (data.statusCode === 200) {
        toast.success(data.message, {
          position: "top-right",
          autoClose: 1500,
          theme: "colored",
        });

        handlePasswordCancel();

        localStorage.clear();
        setTimeout(() => {
          navigate("/signin");
        }, 2000);
      } else {
        // This will handle any 2xx response that is not 201
        showErrorToast(data.message);
      }
    } catch (error) {
      console.error("Error updating password:", error);
      // Server responded with non-2xx status (like 400, 500)

      handleApiError(error, navigate, showErrorToast, "/signin");
    } finally {
      setIsPasswordLoading(false);
    }
  };

  return (
    <Container className="student-second-settings-container">
      <h4 className="student-second-settings-title">My Profile</h4>
      <p className="student-second-settings-subtitle">
        Manage your profile, personal information, and account security.
      </p>

      <div className="student-second-settings-tabs-row">
        <Button
          className={`student-second-settings-tab ${
            activeTab === "profile" ? "active" : ""
          }`}
          onClick={() => setActiveTab("profile")}
        >
          Profile
        </Button>
        <Button
          className={`student-second-settings-tab ${
            activeTab === "security" ? "active" : ""
          }`}
          onClick={() => setActiveTab("security")}
        >
          Security
        </Button>
      </div>

      <div
        className={`student-second-settings-card ${
          activeTab === "profile" ? "student-second-no-border-padding" : ""
        }`}
      >
        {/* profile TAB */}
        {activeTab === "profile" && (
          <>
            <div className="student-second-settings-card">
              {/* Avatar Section */}
              <div className="tutor-profile-avatar-row">
                <div className="tutor-profile-avatar">
                  {avatar ? (
                    <img src={avatar} alt="Avatar" className="avatar-img" />
                  ) : (
                    initialsFromName(profileData.fullName)
                  )}
                </div>

                <div className="tutor-profile-avatar-actions">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handlePhotoUpload}
                  />
                  <Button
                    className="tutor-profile-change-photo-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    Change Photo
                  </Button>
                  <p className="tutor-profile-photo-hint">
                    JPG, JPEG or PNG (max 50KB)
                  </p>
                </div>
              </div>

              <Form onSubmit={handleProfileUpdate} className="mt-4">
                {/* Row 1 */}
                <Row className="g-4 mb-3">
                  {[
                    {
                      label: "Full Name",
                      name: "fullName",
                      type: "text",
                      readOnly: true,
                    },
                    {
                      label: "Email",
                      name: "email",
                      type: "email",
                      readOnly: true,
                    },
                    {
                      label: "Phone Number",
                      name: "phoneNumber",
                      type: "text",
                      readOnly: true,
                    },
                    // { label: "Gender", name: "gender", type: "text" },
                  ].map((field) => (
                    <Col xs={12} sm={6} md={6} lg={3} key={field.name}>
                      <Form.Group
                        controlId={field.name}
                        className="animate-input"
                      >
                        <Form.Label className="student-profile-label">
                          {field.label}
                        </Form.Label>
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={profileData[field.name]}
                          onChange={handleProfileChange}
                          className={`student-profile-input ${
                            profileData[field.name] ? "filled" : ""
                          } ${field.readOnly ? "student-profile-readonly-field" : ""}`}
                          placeholder={`Enter ${field.label}`}
                          readOnly={field.readOnly || false}
                        />
                      </Form.Group>
                    </Col>
                  ))}

                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Form.Group controlId="gender" className="animate-input">
                      <Form.Label className="student-profile-label">
                        Gender
                      </Form.Label>
                      <Form.Select
                        name="gender"
                        value={profileData.gender}
                        onChange={handleProfileChange}
                        className={`student-profile-input form-select ${
                          profileData.gender ? "filled" : ""
                        }`}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Row 2 */}
                <Row className="g-4 mb-3">
                  {[
                    { label: "DOB", name: "dob", type: "date" },
                    { label: "Address", name: "address", type: "text" },
                    { label: "Pincode", name: "pincode", type: "text" },
                    { label: "College", name: "college", type: "text" },

                    // {
                    //   label: "Year of Passing",
                    //   name: "yearOfPassing",
                    //   type: "text",
                    // },
                  ].map((field) => (
                    <Col xs={12} sm={6} md={6} lg={3} key={field.name}>
                      <Form.Group
                        controlId={field.name}
                        className="animate-input"
                      >
                        <Form.Label className="student-profile-label">
                          {field.label}
                        </Form.Label>
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={profileData[field.name]}
                          onChange={handleProfileChange}
                          className={`student-profile-input ${
                            profileData[field.name] ? "filled" : ""
                          }`}
                          placeholder={
                            field.type !== "date" ? `Enter ${field.label}` : ""
                          }
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>

                {/* Row 3 */}
                <Row className="g-4 mb-3">
                  {[
                    {
                      label: "Qualification",
                      name: "qualification",
                      type: "text",
                    },
                  ].map((field) => (
                    <Col xs={12} sm={6} md={6} lg={3} key={field.name}>
                      <Form.Group
                        controlId={field.name}
                        className="animate-input"
                      >
                        <Form.Label className="student-profile-label">
                          {field.label}
                        </Form.Label>
                        <Form.Control
                          type={field.type}
                          name={field.name}
                          value={profileData[field.name]}
                          onChange={handleProfileChange}
                          className={`student-profile-input ${
                            profileData[field.name] ? "filled" : ""
                          }`}
                          placeholder={`Enter ${field.label}`}
                        />
                      </Form.Group>
                    </Col>
                  ))}

                  {/* Year of Passing Dropdown */}
                  <Col xs={12} sm={6} md={6} lg={3}>
                    <Form.Group
                      controlId="yearOfPassing"
                      className="animate-input"
                    >
                      <Form.Label className="student-profile-label">
                        Year of Passing
                      </Form.Label>
                      <Form.Select
                        name="yearOfPassing"
                        value={profileData.yearOfPassing}
                        onChange={handleProfileChange}
                        className={`student-profile-input form-select ${
                          profileData.yearOfPassing ? "filled" : ""
                        }`}
                      >
                        <option value="">Select Year</option>
                        {Array.from(
                          { length: 2040 - 1990 + 1 },
                          (_, i) => 1990 + i,
                        ).map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Buttons */}
                <div className="d-flex justify-content-end gap-2 mt-4">
                  {isProfileChanged() && (
                    <Button
                      type="button"
                      className="student-profile-cancel-btn"
                      onClick={handleProfileCancel}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type="submit"
                    className="student-profile-update-btn"
                    disabled={!isProfileChanged() || isProfileLoading}
                  >
                    {isProfileLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </Form>
            </div>
          </>
        )}

        {/* security TAB */}
        {activeTab === "security" && (
          <>
            <h5 className="student-second-settings-card-title">
              Change Password
            </h5>
            <p className="student-second-settings-card-subtitle">
              Update your password to keep your account secure
            </p>

            <Form onSubmit={handlePasswordUpdate}>
              {/* CURRENT PASSWORD */}
              <Form.Group className="student-second-settings-form-group">
                <Form.Label className="student-second-settings-form-label">
                  Current Password *
                </Form.Label>
                <div className="student-second-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.current ? "text" : "password"}
                    name="currentPassword"
                    placeholder="Enter current password"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className={`student-second-settings-input ${
                      passwordData.currentPassword ? "filled" : ""
                    }`}
                    required
                  />
                  <span
                    className="student-second-settings-password-toggle-icon"
                    onClick={() => toggleShow("current")}
                  >
                    {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* NEW PASSWORD */}
              <Form.Group className="student-second-settings-form-group">
                <Form.Label className="student-second-settings-form-label">
                  New Password *
                </Form.Label>
                <div className="student-second-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.new ? "text" : "password"}
                    name="newPassword"
                    placeholder="Enter new password"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    className={`student-second-settings-input ${
                      passwordData.newPassword ? "filled" : ""
                    }`}
                    required
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                    title="Min 8 chars with uppercase, lowercase, number & symbol"
                  />
                  <span
                    className="student-second-settings-password-toggle-icon"
                    onClick={() => toggleShow("new")}
                  >
                    {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* CONFIRM PASSWORD */}
              <Form.Group className="student-second-settings-form-group">
                <Form.Label className="student-second-settings-form-label">
                  Confirm New Password *
                </Form.Label>
                <div className="student-second-settings-password-input-wrapper">
                  <Form.Control
                    type={showPassword.confirm ? "text" : "password"}
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    className={`student-second-settings-input ${
                      passwordData.confirmPassword ? "filled" : ""
                    }`}
                    required
                    pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}"
                    title="Min 8 chars with uppercase, lowercase, number & symbol"
                  />
                  <span
                    className="student-second-settings-password-toggle-icon"
                    onClick={() => toggleShow("confirm")}
                  >
                    {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
              </Form.Group>

              {/* FOOTER BUTTONS */}
              <div className="student-second-settings-footer">
                <Button
                  className="student-second-settings-cancel-btn"
                  onClick={handlePasswordCancel}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="student-second-settings-update-btn"
                  disabled={isPasswordLoading}
                >
                  {isPasswordLoading ? "Updating..." : "Update Password"}
                </Button>
              </div>
            </Form>
          </>
        )}
      </div>
    </Container>
  );
};

export default StudentProfile;
